/* eslint-disable jsx-a11y/label-has-for */
import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import RedactorEditor from '../RedactorEditor';

import showObject from '../../../../utils/showObject';
import uuid from 'uuid/v4';

const ExplodedModule = ({
  activeLanguage, handlerSetComponents, components, languages, id, reusableComponent, reusableComponentSlugname, slider,
}) => {
  const initialState = {
    type: 'ExplodedModule',
    title: { en: '' },
    subTitle: { en: '' },
    text: { en: '' },
    images: false,
    videos: false,
    reusableCompsList: false,
    bgColor: '#ECECE7',
  };

  const [index, setIndex] = useState();
  const [state, setState] = useState(initialState);
  const [showOptions, setShowOptions] = useState(false);
  const [updatedData, setUpdatedData] = useState(false);

  const [images, setImages] = useState(false);
  const [videos, setVideos] = useState(false);
  const [reusableCompsList, setReusableCompsList] = useState(false);
  const [title, setTitle] = useState({ en: '' });
  const [subTitle, setSubTitle] = useState({ en: '' });
  const [text, setText] = useState({ en: ''});
  const [bgColor, setBgColor] = useState('#ECECE7');

  useEffect(() => {
    const componentIndex = components.findIndex(obj => obj.id === id);
    const { component } = components[componentIndex];
    if (reusableComponent) {
      setState(reusableComponent);
      if (!component) handlerSetComponents({ component: { key: reusableComponentSlugname } }, componentIndex);
    } else {
      setIndex(componentIndex);
      if (component) {

        const {
          title: currentTitle,
          subTitle: currentSubTitle,
          text: currentText,
          images: currentImages,
          videos: currentVideos,
          reusableCompsList: currentReusableCompsList,
          bgColor: currentBgColor,
        } = component;

        setState(component);
        currentTitle && setTitle(currentTitle);
        currentSubTitle && setSubTitle(currentSubTitle);
        setText(currentText);
        setImages(currentImages);
        // Blocks saved before videos existed have no `videos` key.
        setVideos(currentVideos || false);
        setReusableCompsList(currentReusableCompsList);
        setBgColor(currentBgColor);

      } else {
        const componentObj = {
          id,
          component: state,
        };

        handlerSetComponents(componentObj, componentIndex);
      }
    }
  }, [components]);

  useEffect(() => {
    const componentIndex = components.findIndex(obj => obj.id === id);
    const construction = {
      type: state.type,
      compId: state.compId || '',
      title,
      subTitle,
      text,
      images,
      videos,
      reusableCompsList,
      bgColor,
    }

    handlerSetComponents({ component: construction }, componentIndex);
  }, [
    title,
    subTitle,
    text,
    images,
    videos,
    reusableCompsList,
    bgColor,
    updatedData,
  ]);

  const textChange = (value) => {
    text[activeLanguage] = value
    setText({ ...text });
  }

  const titleChange = (e) => {
    title[activeLanguage] = e.target.value
    setTitle({ ...title });
  }

  const subTitleChange = (value) => {
    subTitle[activeLanguage] = value
    setSubTitle({ ...subTitle });
  }

  const imagesTitleChange = (e) => {
    const itemIndex = e.target.getAttribute('itemindex');
    const { name, value } = e.target;
    images[itemIndex][name][activeLanguage] = value
    setImages(images);
    setUpdatedData(uuid());
  }

  const videosTitleChange = (e) => {
    const itemIndex = e.target.getAttribute('itemindex');
    const { name, value } = e.target;
    videos[itemIndex][name][activeLanguage] = value
    setVideos(videos);
    setUpdatedData(uuid());
  }

  const pushAnItemToVideos = () => {
    let currentVideos = videos;
    if (currentVideos) {
      currentVideos.push({
        url: { en: '' },
        caption: { en: '' },
      })
    } else {
      currentVideos = [{
        url: { en: '' },
        caption: { en: '' },
      }];
    };

    setVideos(currentVideos);
    setUpdatedData(uuid());
  }

  const removeFromVideos = index => {
    videos.splice(index, 1)
    if (videos.length > 0) {
      setVideos(videos);
    } else {
      setVideos(false);
    }
    setUpdatedData(uuid());
  }

  const pushAnItemToImages = () => {
    let currentImages = images;
    if (currentImages) {
      currentImages.push({
        image: { en: '' },
        caption: { en: '' },
      })
    } else {
      currentImages = [{
        image: { en: '' },
        caption: { en: '' },
      }];

    };

    setImages(currentImages);
    setUpdatedData(uuid());
  }

  const keyTitleChange = (e) => {
    const itemIndex = e.target.getAttribute('itemindex');
    const { name, value } = e.target;
    reusableCompsList[itemIndex][name] = value
    setReusableCompsList(reusableCompsList);
    setUpdatedData(uuid());
  }

  const pushAnItemToReusableCompsList = () => {
    let currentReusableCompsList = reusableCompsList;
    if (currentReusableCompsList) {
      currentReusableCompsList.push({
        key: '',
      })
    } else {
      currentReusableCompsList = [{
        key: '',
      }];
    };

    setReusableCompsList(currentReusableCompsList);
    setUpdatedData(uuid());
  }

  const removeFromReusableCompsList = index => {
    reusableCompsList.splice(index, 1)
    if (reusableCompsList.length > 0) {
      setReusableCompsList(reusableCompsList);
    } else {
      setReusableCompsList(false);
    }
    setUpdatedData(uuid());
  }

  const onDragEnd = (result) => {
    const { source, destination } = result;
    console.log("onDragEnd -> source", source)
    console.log("onDragEnd -> destination", destination)

    // dropped outside the list
    if (!destination) {
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) return

    // Each droppable owns exactly one list. This used to be an if/else where
    // anything that was not the images list fell through to reusableCompsList —
    // with a third list on the block that would silently reorder the wrong one.
    const listsByDroppableId = {
      selected: [images, setImages],
      selectedVideos: [videos, setVideos],
      selectedReusable: [reusableCompsList, setReusableCompsList],
    };

    switch (source.droppableId) {
      case destination.droppableId: {
        const entry = listsByDroppableId[destination.droppableId];
        if (!entry || !entry[0]) break;
        const [list, setList] = entry;

        [list[source.index], list[destination.index]] =
          [list[destination.index], list[source.index]];
        setList(list);
        setUpdatedData(uuid());
        break;
      }

      default: {
        break;
      }
    }
  };

  const removeFromImages = index => {
    images.splice(index, 1)
    if (images.length > 0) {
      setImages(images);
    } else {
      setImages(false);
    }
    setUpdatedData(uuid());
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
    <div className="flex justify-start flex-col text-black">
      {/* Single Form */}
      <div className="pb-4 w-full">
          <div className="w-full md:mb-0 text-black">
            <label
              className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              Title
                        </label>
            <input
              className="appearance-none block
                      bg-gray-200 text-gray-700 border border-red-500
                      rounded py-3 px-4 w-full leading-tight focus:outline-none
                      focus:bg-white"
              type="text"
              itemindex={index}
              name="title"
              onChange={titleChange}
              value={title[activeLanguage]}
            />
          </div>
          <div className="w-full md:mb-0 text-black">
            <label
              className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              SubTitle
                        </label>
            <RedactorEditor
              text={subTitle[activeLanguage]}
              setText={subTitleChange}
              id={`subtitle_${id}`}
            />
            {/* <input
              className="appearance-none block
                      bg-gray-200 text-gray-700 border border-red-500
                      rounded py-3 px-4 w-full leading-tight focus:outline-none
                      focus:bg-white"
              type="text"
              itemindex={index}
              name="subTitle"
              onChange={subTitleChange}
              value={subTitle[activeLanguage]}
            /> */}
          </div>
          <div className="w-full md:mb-0 mt-2">
            <label
              className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              Text
            </label>
            <RedactorEditor
              text={text[activeLanguage]}
              setText={textChange}
              id={id}
            />
            {/* <textarea
              className="appearance-none block
                      bg-gray-200 text-gray-700 border border-red-500
                      rounded py-3 px-4 w-full leading-tight focus:outline-none
                      focus:bg-white min-h-50"
              type="text"
              itemindex={index}
              name="text"
              onChange={titleChange}
              value={text[activeLanguage]}
            /> */}
          </div>
        </div>

      </div>
      {/* End */}
      {/* Images OPTIONS */}
      <label
        className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 text-black mt-2"
      >
      Images
      </label>
      <Droppable droppableId="selected">
        {(provided, snapshot) => (
          <div ref={provided.innerRef}
            className={`${snapshot.isDraggingOver ? 'bg-grey' : 'bg-grey-lighter'} mb-4 rounded w-full p-2 relative text-black`}>
            {images && images.map((imageItem, index) => {
              const {image, caption} = imageItem;
              return (
                <Draggable
                  key={`${id}_${index}`}
                  draggableId={`${id}_${index}`}
                  index={index}
                  activeLanguage={activeLanguage}>
                  {(provided, snapshot, component) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`relative bg-gray-200 bg-white min-h-30 mb-2 rounded w-full p-2`}
                      style={provided.draggableProps.style}
                    >
                      <div className="flex flex-wrap mb-2">
                        <div className="bg-grey-light cursor-pointer font-bold mr-1 p-2 px-4 rounded text-center">{index + 1}</div>
                        <div className="bg-grey-light cursor-pointer text-center flex-2 p-2 rounded" {...provided.dragHandleProps}> =</div>
                        <div className="cursor-pointer p-2 rounded" onClick={()=> removeFromImages(index)}><span className="font-icon icon-close-btn1" /></div>
                      </div>
                      <div className="w-full md:w-full md:mb-0 mt-2">
                        <label
                          className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                          >
                          Image
                        </label>
                        <input
                          className="appearance-none block
                      bg-gray-200 text-gray-700 border border-red-500
                      rounded py-3 px-4 w-full leading-tight focus:outline-none
                      focus:bg-white"
                            itemindex={index}
                            name="image"
                          type="text"
                          onChange={imagesTitleChange}
                          value={image[activeLanguage]}
                        />
                      </div>
                      <div className="w-full md:w-full md:mb-0 mt-2">
                        <label
                          className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                        >
                          Caption
                        </label>
                        <input
                          className="appearance-none block
                      bg-gray-200 text-gray-700 border border-red-500
                      rounded py-3 px-4 w-full leading-tight focus:outline-none
                      focus:bg-white"
                          itemindex={index}
                          name="caption"
                          type="text"
                          onChange={imagesTitleChange}
                          value={caption[activeLanguage]}
                        />
                      </div>
                    </div>

                  )}
                </Draggable>
              )
            })}
              {provided.placeholder}
          </div>

        )}

      </Droppable>
      <div className="hover:bg-blue-light hover:text-white rounded w-full">
        <div
          className={`block appearance-none w-full
                bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8
                rounded leading-tight focus:outline-none focus:bg-white
                focus:border-gray-500 text-center text-black`}
          onClick={pushAnItemToImages}
        >+</div>
      </div>
      {/* Images OPTIONS */}
      {/* Videos OPTIONS */}
      <label
        className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 text-black mt-6"
      >
      Videos
      </label>
      <Droppable droppableId="selectedVideos">
        {(provided, snapshot) => (
          <div ref={provided.innerRef}
            className={`${snapshot.isDraggingOver ? 'bg-grey' : 'bg-grey-lighter'} mb-4 rounded w-full p-2 relative text-black`}>
            {videos && videos.map((videoItem, index) => {
              const { url, caption } = videoItem;
              return (
                <Draggable
                  key={`${id}_video_${index}`}
                  draggableId={`${id}_video_${index}`}
                  index={index}
                  activeLanguage={activeLanguage}>
                  {(provided, snapshot, component) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`relative bg-gray-200 bg-white min-h-30 mb-2 rounded w-full p-2`}
                      style={provided.draggableProps.style}
                    >
                      <div className="flex flex-wrap mb-2">
                        <div className="bg-grey-light cursor-pointer font-bold mr-1 p-2 px-4 rounded text-center">{index + 1}</div>
                        <div className="bg-grey-light cursor-pointer text-center flex-2 p-2 rounded" {...provided.dragHandleProps}> =</div>
                        <div className="cursor-pointer p-2 rounded" onClick={()=> removeFromVideos(index)}><span className="font-icon icon-close-btn1" /></div>
                      </div>
                      <div className="w-full md:w-full md:mb-0 mt-2">
                        <label
                          className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                        >
                          Video URL (YouTube, Shorts or Vimeo)
                        </label>
                        <input
                          className="appearance-none block
                      bg-gray-200 text-gray-700 border border-red-500
                      rounded py-3 px-4 w-full leading-tight focus:outline-none
                      focus:bg-white"
                          itemindex={index}
                          name="url"
                          type="text"
                          onChange={videosTitleChange}
                          value={url[activeLanguage] || ''}
                        />
                      </div>
                      <div className="w-full md:w-full md:mb-0 mt-2">
                        <label
                          className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                        >
                          Caption
                        </label>
                        <input
                          className="appearance-none block
                      bg-gray-200 text-gray-700 border border-red-500
                      rounded py-3 px-4 w-full leading-tight focus:outline-none
                      focus:bg-white"
                          itemindex={index}
                          name="caption"
                          type="text"
                          onChange={videosTitleChange}
                          value={caption[activeLanguage] || ''}
                        />
                      </div>
                    </div>

                  )}
                </Draggable>
              )
            })}
              {provided.placeholder}
          </div>

        )}

      </Droppable>
      <div className="hover:bg-blue-light hover:text-white rounded w-full">
        <div
          className={`block appearance-none w-full
                bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8
                rounded leading-tight focus:outline-none focus:bg-white
                focus:border-gray-500 text-center text-black`}
          onClick={pushAnItemToVideos}
        >+</div>
      </div>
      {/* Videos OPTIONS */}
      {/* Reusable OPTIONS */}
      <label
        className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 text-black mt-6"
      >
        Reusable Components List
      </label>
      <Droppable droppableId="selectedReusable">
          {(provided, snapshot) => (
            <div ref={provided.innerRef}
              className={`${snapshot.isDraggingOver ? 'bg-grey' : 'bg-grey-lighter'} mb-4 rounded w-full p-2 relative text-black`}>
              {reusableCompsList && reusableCompsList.map((reusableCompsListItem, index) => {
                const { key } = reusableCompsListItem;
                return (
                  <Draggable
                    key={`Reusable_${id}_${index}`}
                    draggableId={`Reusable_${id}_${index}`}
                    index={index}
                    activeLanguage={activeLanguage}>
                    {(provided, snapshot, component) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`relative bg-gray-200 bg-white min-h-30 mb-2 rounded w-full p-2`}
                        style={provided.draggableProps.style}
                      >
                        <div className="flex flex-wrap mb-2">
                          <div className="bg-grey-light cursor-pointer font-bold mr-1 p-2 px-4 rounded text-center">{index + 1}</div>
                          <div className="bg-grey-light cursor-pointer text-center flex-2 p-2 rounded" {...provided.dragHandleProps}> =</div>
                          <div className="cursor-pointer p-2 rounded" onClick={() => removeFromReusableCompsList(index)}><span className="font-icon icon-close-btn1" /></div>
                        </div>
                        <div className="w-full md:w-full md:mb-0 mt-2">
                          <label
                            className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                          >
                            Component key
                        </label>
                          <input
                            className="appearance-none block
                      bg-gray-200 text-gray-700 border border-red-500
                      rounded py-3 px-4 w-full leading-tight focus:outline-none
                      focus:bg-white"
                            itemindex={index}
                            name="key"
                            type="text"
                            onChange={keyTitleChange}
                            value={key}
                          />
                        </div>
                      </div>

                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </div>

          )}

        </Droppable>
      <div className="hover:bg-blue-light hover:text-white rounded w-full">
        <div
          className={`block appearance-none w-full
                bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8
                rounded leading-tight focus:outline-none focus:bg-white
                focus:border-gray-500 text-center text-black`}
          onClick={pushAnItemToReusableCompsList}
        >+</div>
      </div>
      {/* Images OPTIONS */}
    </DragDropContext>
  );
};

ExplodedModule.propTypes = {
  activeLanguage: PropTypes.string.isRequired,
  handlerSetComponents: PropTypes.func.isRequired,
  components: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  id: PropTypes.string.isRequired,
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  reusableComponent: PropTypes.shape(),
  slider: PropTypes.bool,
};
ExplodedModule.defaultProps = {
  reusableComponent: null,
  slider: false,
};

export default ExplodedModule;
